import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, Building2, Calculator, FileText, Settings, LogOut, Home, CheckCircle2, Workflow, BarChart3, Inbox,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Pannello di controllo", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/lead", label: "Lead", icon: Inbox, testid: "nav-leads" },
  { to: "/contatti", label: "Contatti", icon: Users, testid: "nav-contacts" },
  { to: "/immobili", label: "Immobili", icon: Building2, testid: "nav-properties" },
  { to: "/operazioni-in-corso", label: "Operazioni attive", icon: Workflow, testid: "nav-operations" },
  { to: "/valutazione", label: "Valu-Art", icon: Calculator, testid: "nav-evaluation" },
  { to: "/preventivi", label: "Preventivi", icon: FileText, testid: "nav-quotes" },
  { to: "/operazioni", label: "Operazioni concluse", icon: CheckCircle2, testid: "nav-concluded", end: true },
  { to: "/impostazioni", label: "Impostazioni aziendali", icon: Settings, testid: "nav-settings", adminOnly: true },
  { to: "/performance", label: "Performance", icon: BarChart3, testid: "nav-performance" },
];

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-[260px] shrink-0 hidden lg:flex flex-col border-r border-border bg-white">
        <div className="h-16 flex items-center gap-2.5 px-6 border-b border-border">
          <div className="h-9 w-9 rounded-lg bg-primary text-primary-foreground grid place-items-center">
            <Home className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display font-bold text-lg leading-none text-slate-900">InvestArt</div>
            <div className="text-[11px] text-muted-foreground">Reggio Emilia</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.filter((n) => !n.adminOnly || isAdmin).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
                  isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-slate-600 hover:bg-muted hover:text-slate-900"
                }`
              }
            >
              <n.icon className="h-[18px] w-[18px]" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-9 w-9 rounded-full bg-secondary text-secondary-foreground grid place-items-center text-sm font-semibold">
              {user?.name?.split(" ").map((s) => s[0]).join("").slice(0, 2)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-900 truncate">{user?.name}</div>
              <div className="text-[11px] text-muted-foreground">{isAdmin ? "Amministratore" : "Agente"}</div>
            </div>
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn" title="Esci">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 shrink-0 flex items-center justify-between px-4 md:px-8 border-b border-border bg-white/80 backdrop-blur-md sticky top-0 z-20">
          <div className="lg:hidden font-display font-bold text-lg text-primary">InvestArt</div>
          <div className="hidden lg:block text-sm text-muted-foreground">Gestionale operativo interno · EUR · Europe/Rome</div>
          <div className="flex items-center gap-3">
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${isAdmin ? "bg-primary/10 text-primary" : "bg-secondary/10 text-secondary"}`} data-testid="role-pill">
              {isAdmin ? "Admin" : "Agente"}
            </span>
            <Button variant="ghost" size="sm" className="lg:hidden" onClick={logout} data-testid="logout-btn-mobile">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>
        <nav className="lg:hidden flex items-center gap-1 overflow-x-auto border-b border-border bg-white px-3 py-2">
          {NAV.filter((n) => !n.adminOnly || isAdmin).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`m-${n.testid}`}
              className={({ isActive }) =>
                `flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                  isActive ? "bg-primary text-primary-foreground" : "text-slate-600 hover:bg-muted"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <main className="flex-1 p-4 md:p-8 max-w-[1500px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
